import { useEffect, useMemo } from "react";

/**
 * useConfidenceTrend — caches the last 24h of rain-confidence scores per
 * location in localStorage and returns the delta between *now* and a baseline
 * sample ≈60 minutes ago. Used by `RainConfidenceRing` to render a tiny
 * ↗/↘/→ arrow next to the score, turning the snapshot into a forecaster's
 * tool ("risk rising vs an hour ago").
 *
 * Storage shape:
 *   { "<lat,lon>": [{ ts, score }, …], … }
 *
 * Behaviour:
 *   • New score is appended to its location's history on every change.
 *   • Entries older than 24h are pruned on every write.
 *   • Baseline = newest entry that is ≥30min old (target ≈60min). If no
 *     such entry exists we have no trend yet → returns null.
 */
const LS_KEY = "nowcast.confidenceHistory.v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h
const MIN_BASELINE_AGE_MS = 30 * 60 * 1000; // 30 min — minimum gap for a meaningful delta
const TARGET_BASELINE_AGE_MS = 60 * 60 * 1000; // 60 min — preferred gap

function readStore() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(store));
  } catch {
    /* quota exceeded — ignore */
  }
}

function locationKey(latitude, longitude) {
  if (latitude == null || longitude == null) return null;
  // 2-decimal precision (~1.1km) so a small GPS drift still compares against
  // the same baseline.
  return `${latitude.toFixed(2)},${longitude.toFixed(2)}`;
}

export default function useConfidenceTrend(score, latitude, longitude) {
  const key = locationKey(latitude, longitude);

  // Append every new score sample to localStorage and prune old entries
  useEffect(() => {
    if (score == null || !key) return;
    const now = Date.now();
    const store = readStore();
    const arr = Array.isArray(store[key]) ? store[key] : [];

    // De-dupe: if last entry is <2min old, replace it instead of appending.
    const last = arr[arr.length - 1];
    if (last && now - last.ts < 2 * 60 * 1000) {
      arr[arr.length - 1] = { ts: now, score };
    } else {
      arr.push({ ts: now, score });
    }

    // Prune anything older than 24h
    const pruned = arr.filter((e) => now - e.ts <= MAX_AGE_MS);
    store[key] = pruned;
    writeStore(store);
  }, [score, key]);

  return useMemo(() => {
    if (score == null || !key) return null;
    const now = Date.now();
    const store = readStore();
    const arr = Array.isArray(store[key]) ? store[key] : [];
    if (arr.length < 1) return null;

    // Full 24h history series (sorted oldest → newest), used for the sparkline.
    const history = arr
      .filter((e) => e && typeof e.ts === "number" && typeof e.score === "number")
      .sort((a, b) => a.ts - b.ts);

    // Find the entry whose age is closest to TARGET_BASELINE_AGE_MS but at
    // least MIN_BASELINE_AGE_MS old. Walk backwards from oldest to newest
    // and pick the one with the smallest |age - 60min|.
    let baseline = null;
    let bestDistance = Infinity;
    for (const e of arr) {
      const age = now - e.ts;
      if (age < MIN_BASELINE_AGE_MS) continue;
      const distance = Math.abs(age - TARGET_BASELINE_AGE_MS);
      if (distance < bestDistance) {
        bestDistance = distance;
        baseline = e;
      }
    }

    let trendInfo = null;
    if (baseline) {
      const delta = score - baseline.score;
      const ageMin = Math.round((now - baseline.ts) / 60000);
      let direction = "flat";
      if (delta >= 5) direction = "up";
      else if (delta <= -5) direction = "down";
      trendInfo = { delta, direction, baselineScore: baseline.score, ageMin };
    }

    if (!trendInfo && history.length < 2) return null;
    return { ...(trendInfo || {}), history };
  }, [score, key]);
}
